import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Cog, MessageCircle, Settings as SettingsIcon, Shield, User, Users } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { ROLES, isActive } from "../lib/auth";

function initials(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
}

export default function WorkspaceSwitcher() {
    const {
        teachers,
        currentUser,
        isAdmin,
        workspaceMode,
        setCurrentUserId,
        setWorkspaceMode,
    } = useAuth();
    const [open, setOpen] = useState(false);

    if (!currentUser) return null;

    const teacherUsers = teachers.filter(
        (t) => t.role === ROLES.TEACHER && isActive(t),
    );
    const adminUsers = teachers.filter(
        (t) => t.role === ROLES.ADMIN && isActive(t),
    );

    const activeTeacherName =
        isAdmin && workspaceMode !== "all"
            ? teachers.find((t) => t.id === workspaceMode)?.name
            : null;

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="workspace-switcher-trigger"
                    className="group inline-flex items-center gap-2 sm:gap-3 h-11 pl-1 pr-3 rounded-2xl border border-mist bg-white hover:bg-mist/50 transition-all"
                >
                    <span
                        className={`w-9 h-9 rounded-xl font-display font-bold text-sm flex items-center justify-center ${
                            isAdmin
                                ? "bg-ink text-white"
                                : "bg-brand-soft text-brand"
                        }`}
                    >
                        {initials(currentUser.name)}
                    </span>
                    <div className="hidden xl:block text-left leading-tight">
                        <div className="font-bold text-xs text-slate-900 truncate max-w-[140px]">
                            {currentUser.name}
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 flex items-center gap-1 whitespace-nowrap">
                            {isAdmin ? (
                                <>
                                    <Shield size={10} strokeWidth={3} />
                                    Admin Mode
                                    <span className="text-slate-300">·</span>
                                    {activeTeacherName ? (
                                        <span className="text-[#3D6385] normal-case tracking-normal">
                                            viewing {activeTeacherName}
                                        </span>
                                    ) : (
                                        <span className="text-[#3D6385] normal-case tracking-normal">
                                            All Studio
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <User size={10} strokeWidth={3} />
                                    Teacher Mode
                                </>
                            )}
                        </div>
                    </div>
                    <ChevronDown
                        size={16}
                        strokeWidth={2.5}
                        className="text-slate-400 group-hover:text-slate-600"
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="w-72 rounded-2xl p-2"
                data-testid="workspace-switcher-menu"
            >
                <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 px-2 pt-2">
                    Viewing as
                </DropdownMenuLabel>
                {teacherUsers.map((t) => (
                    <DropdownMenuItem
                        key={t.id}
                        onClick={() => setCurrentUserId(t.id)}
                        data-testid={`switch-user-${t.id}`}
                        className="rounded-xl cursor-pointer flex items-center gap-2 py-2"
                    >
                        <span className="w-7 h-7 rounded-lg bg-brand-soft text-brand font-bold text-xs flex items-center justify-center">
                            {initials(t.name)}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm truncate">{t.name}</div>
                            <div className="text-[10px] uppercase tracking-wide text-slate-500">
                                Teacher
                            </div>
                        </div>
                        {currentUser.id === t.id && (
                            <Check size={14} className="text-[#3D6385]" strokeWidth={3} />
                        )}
                    </DropdownMenuItem>
                ))}
                {adminUsers.map((t) => (
                    <DropdownMenuItem
                        key={t.id}
                        onClick={() => setCurrentUserId(t.id)}
                        data-testid={`switch-user-${t.id}`}
                        className="rounded-xl cursor-pointer flex items-center gap-2 py-2"
                    >
                        <span className="w-7 h-7 rounded-lg bg-ink text-white font-bold text-xs flex items-center justify-center">
                            {initials(t.name)}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm truncate">{t.name}</div>
                            <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                                <Shield size={9} strokeWidth={3} />
                                Admin
                            </div>
                        </div>
                        {currentUser.id === t.id && (
                            <Check size={14} className="text-[#3D6385]" strokeWidth={3} />
                        )}
                    </DropdownMenuItem>
                ))}

                {isAdmin && (
                    <>
                        <DropdownMenuSeparator className="my-2" />
                        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 px-2 flex items-center gap-1">
                            <Users size={11} strokeWidth={3} />
                            Workspace filter
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onClick={() => setWorkspaceMode("all")}
                            data-testid="workspace-all"
                            className="rounded-xl cursor-pointer flex items-center justify-between py-2"
                        >
                            <span className="font-bold text-sm">All workspaces</span>
                            {workspaceMode === "all" && (
                                <Check size={14} className="text-[#3D6385]" strokeWidth={3} />
                            )}
                        </DropdownMenuItem>
                        {teacherUsers.map((t) => (
                            <DropdownMenuItem
                                key={t.id}
                                onClick={() => setWorkspaceMode(t.id)}
                                data-testid={`workspace-${t.id}`}
                                className="rounded-xl cursor-pointer flex items-center justify-between py-2"
                            >
                                <span className="text-sm">
                                    <span className="font-bold">{t.name}</span>
                                    <span className="text-slate-400">'s studio</span>
                                </span>
                                {workspaceMode === t.id && (
                                    <Check
                                        size={14}
                                        className="text-[#3D6385]"
                                        strokeWidth={3}
                                    />
                                )}
                            </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator className="my-2" />
                        <Link to="/admin/teachers" onClick={() => setOpen(false)}>
                            <DropdownMenuItem
                                className="rounded-xl cursor-pointer flex items-center gap-2 py-2 text-slate-700 font-bold"
                                data-testid="menu-manage-teachers"
                            >
                                <Cog size={14} strokeWidth={2.8} />
                                Manage Teachers
                            </DropdownMenuItem>
                        </Link>
                        <Link to="/admin/settings" onClick={() => setOpen(false)}>
                            <DropdownMenuItem
                                className="rounded-xl cursor-pointer flex items-center gap-2 py-2 text-slate-700 font-bold"
                                data-testid="menu-studio-settings"
                            >
                                <SettingsIcon size={14} strokeWidth={2.8} />
                                Studio Settings
                            </DropdownMenuItem>
                        </Link>
                        <Link to="/admin/feedback" onClick={() => setOpen(false)}>
                            <DropdownMenuItem
                                className="rounded-xl cursor-pointer flex items-center gap-2 py-2 text-slate-700 font-bold"
                                data-testid="menu-feedback"
                            >
                                <MessageCircle size={14} strokeWidth={2.8} />
                                Feedback
                            </DropdownMenuItem>
                        </Link>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
